/**
 * GS1 Parser & ZPL / RFID Generator para R-MAX Indústria
 * Conformidade com GS1 General Specifications (GS1-128, GS1 DataMatrix),
 * ISO/IEC 15434, ZPL II (Zebra Programming Language) e EPC Tag Data Standard (SGTIN-96)
 */

export interface Gs1ParsedData {
  raw: string;
  gtin?: string;
  batchNumber?: string;
  expiryDate?: string;     // YYYY-MM-DD
  productionDate?: string; // YYYY-MM-DD
  serialNumber?: string;
  quantity?: number;
  netWeightKg?: number;
  hriText: string;
  isValid: boolean;
}

export interface ZplLabelOptions {
  companyName: string;
  productCode: string;
  productName: string;
  batchNumber: string;
  quantity: number;
  unit: string;
  productionDate?: string;
  expiryDate?: string;
  gtin?: string;
  qaStatus?: 'approved' | 'quarantine' | 'rejected' | 'conditionally_approved';
  rfidHex?: string;
  serialNumber?: number;
  dpi?: 203 | 300;
  widthMm?: number;
  heightMm?: number;
  copies?: number;
}

/**
 * Sanitiza campos de texto para prevenir injeção de comandos ZPL (^ e ~)
 * e neutraliza quebras de linha e caracteres de controle
 */
export function escapeZpl(text: string | number | undefined | null): string {
  if (text === undefined || text === null) return '';
  return String(text)
    .replace(/[\^~\\_]/g, ' ')
    .replace(/[\r\n\t]/g, ' ')
    .trim();
}

/**
 * Converte data YYMMDD do padrão GS1 para YYYY-MM-DD
 */
export function parseGs1Date(yymmdd: string): string | undefined {
  if (!yymmdd || yymmdd.length !== 6 || !/^\d{6}$/.test(yymmdd)) {
    return undefined;
  }
  const yy = parseInt(yymmdd.substring(0, 2), 10);
  const mm = parseInt(yymmdd.substring(2, 4), 10);
  const dd = parseInt(yymmdd.substring(4, 6), 10);

  if (mm < 1 || mm > 12) return undefined;

  // Regra GS1: Anos 51-99 são 19xx; Anos 00-50 são 20xx
  const fullYear = yy >= 51 ? 1900 + yy : 2000 + yy;

  // Se o dia for 00, o padrão GS1 indica o último dia do mês
  let validDay = dd;
  if (dd === 0) {
    const lastDayOfMonth = new Date(fullYear, mm, 0).getDate();
    validDay = lastDayOfMonth;
  }

  const mmStr = String(mm).padStart(2, '0');
  const ddStr = String(validDay).padStart(2, '0');
  return `${fullYear}-${mmStr}-${ddStr}`;
}

/**
 * Converte data YYYY-MM-DD para o formato GS1 YYMMDD
 */
export function formatGs1Date(isoDate?: string): string {
  if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    return '000000';
  }
  const [year, month, day] = isoDate.split('-');
  return `${year.substring(2, 4)}${month}${day}`;
}

/**
 * Parser de Código de Barras GS1 (GS1-128 / GS1 DataMatrix / GS1 QR)
 * Suporta strings com parênteses HRI e leituras brutas de leitor ótico com FNC1 (\x1d)
 */
export function parseGs1Barcode(input: string): Gs1ParsedData {
  const trimmed = (input || '').trim();
  const result: Gs1ParsedData = {
    raw: trimmed,
    hriText: '',
    isValid: false
  };

  if (!trimmed) return result;

  // 1. Caso a string já contenha os parênteses do HRI: (01)...(10)...
  if (trimmed.includes('(') && trimmed.includes(')')) {
    const aiRegex = /\((\d{2,4})\)([^()]+)/g;
    let match: RegExpExecArray | null;
    let foundAny = false;

    while ((match = aiRegex.exec(trimmed)) !== null) {
      foundAny = true;
      const ai = match[1];
      const val = match[2].trim();

      applyApplicationIdentifier(result, ai, val);
    }

    if (foundAny) {
      result.isValid = true;
      result.hriText = buildHriString(result);
      return result;
    }
  }

  // 2. Leitura bruta de scanner: Pode vir com prefixo AIM tipo ]C1 ou ]d2, e separador FNC1 (\x1d ou chr 29)
  let cleanStream = trimmed;
  if (cleanStream.startsWith(']C1') || cleanStream.startsWith(']d2') || cleanStream.startsWith(']Q3')) {
    cleanStream = cleanStream.substring(3);
  }

  // O separador de grupo ASCII 29 (FNC1) delimita campos de tamanho variável.
  // Normaliza representações de FNC1 / GS (ASCII 29, hex escape _1D comum em ZPL DataMatrix, ou <GS>)
  const FNC1 = String.fromCharCode(29);
  cleanStream = cleanStream.replace(/_1[dD]/g, FNC1).replace(/<GS>/gi, FNC1);

  let cursor = 0;
  let parsedCount = 0;

  while (cursor < cleanStream.length) {
    if (cleanStream[cursor] === FNC1) {
      cursor++;
      continue;
    }

    // Identificadores de Aplicação comuns
    // AI 01: GTIN-14 (Fixo 14 dígitos)
    if (cleanStream.startsWith('01', cursor)) {
      const gtin = cleanStream.substring(cursor + 2, cursor + 2 + 14);
      result.gtin = gtin;
      cursor += 2 + 14;
      parsedCount++;
      continue;
    }

    // AI 17: Data de Validade (Fixo 6 dígitos YYMMDD)
    if (cleanStream.startsWith('17', cursor)) {
      const yymmdd = cleanStream.substring(cursor + 2, cursor + 2 + 6);
      result.expiryDate = parseGs1Date(yymmdd);
      cursor += 2 + 6;
      parsedCount++;
      continue;
    }

    // AI 11: Data de Fabricação (Fixo 6 dígitos YYMMDD)
    if (cleanStream.startsWith('11', cursor)) {
      const yymmdd = cleanStream.substring(cursor + 2, cursor + 2 + 6);
      result.productionDate = parseGs1Date(yymmdd);
      cursor += 2 + 6;
      parsedCount++;
      continue;
    }

    // AI 10: Número de Lote (Variável, até 20 caracteres alfanuméricos)
    if (cleanStream.startsWith('10', cursor)) {
      const start = cursor + 2;
      let end = cleanStream.indexOf(FNC1, start);
      if (end === -1) end = Math.min(start + 20, cleanStream.length);
      result.batchNumber = cleanStream.substring(start, end);
      cursor = end;
      parsedCount++;
      continue;
    }

    // AI 21: Número de Série (Variável, até 20 caracteres)
    if (cleanStream.startsWith('21', cursor)) {
      const start = cursor + 2;
      let end = cleanStream.indexOf(FNC1, start);
      if (end === -1) end = Math.min(start + 20, cleanStream.length);
      result.serialNumber = cleanStream.substring(start, end);
      cursor = end;
      parsedCount++;
      continue;
    }

    // AI 30: Quantidade Variável de Itens (até 8 dígitos)
    if (cleanStream.startsWith('30', cursor)) {
      const start = cursor + 2;
      let end = cleanStream.indexOf(FNC1, start);
      if (end === -1) end = Math.min(start + 8, cleanStream.length);
      const qtyStr = cleanStream.substring(start, end);
      const parsedQty = parseFloat(qtyStr);
      if (!isNaN(parsedQty)) result.quantity = parsedQty;
      cursor = end;
      parsedCount++;
      continue;
    }

    // AI 310x: Peso Líquido em Kg (x = casas decimais, 6 dígitos fixos após)
    if (/^310\d/.test(cleanStream.substring(cursor, cursor + 4))) {
      const decimals = parseInt(cleanStream[cursor + 3], 10);
      const weightRaw = cleanStream.substring(cursor + 4, cursor + 4 + 6);
      const weightNum = parseInt(weightRaw, 10);
      if (!isNaN(weightNum)) {
        result.netWeightKg = weightNum / Math.pow(10, decimals);
      }
      cursor += 4 + 6;
      parsedCount++;
      continue;
    }

    // Se não bateu com nenhum AI conhecido, avança 1 caracter
    cursor++;
  }

  if (parsedCount > 0) {
    result.isValid = true;
    result.hriText = buildHriString(result);
  } else {
    // Fallback: Se for um código de lote simples (ex: "LOT-2026-A") ou código de barras direto
    result.batchNumber = trimmed;
    result.hriText = trimmed;
    result.isValid = trimmed.length >= 3;
  }

  return result;
}

function applyApplicationIdentifier(result: Gs1ParsedData, ai: string, val: string) {
  switch (ai) {
    case '01':
      result.gtin = val;
      break;
    case '10':
      result.batchNumber = val;
      break;
    case '17':
      result.expiryDate = parseGs1Date(val);
      break;
    case '11':
      result.productionDate = parseGs1Date(val);
      break;
    case '21':
      result.serialNumber = val;
      break;
    case '30':
    case '37': {
      const q = parseFloat(val);
      if (!isNaN(q)) result.quantity = q;
      break;
    }
    default:
      if (ai.startsWith('310')) {
        const decimals = parseInt(ai[3], 10);
        const w = parseInt(val, 10);
        if (!isNaN(w)) result.netWeightKg = w / Math.pow(10, decimals);
      }
      break;
  }
}

function buildHriString(data: Gs1ParsedData): string {
  const parts: string[] = [];
  if (data.gtin) parts.push(`(01)${data.gtin}`);
  if (data.batchNumber) parts.push(`(10)${data.batchNumber}`);
  if (data.expiryDate) parts.push(`(17)${formatGs1Date(data.expiryDate)}`);
  if (data.productionDate) parts.push(`(11)${formatGs1Date(data.productionDate)}`);
  if (data.serialNumber) parts.push(`(21)${data.serialNumber}`);
  if (data.quantity) parts.push(`(30)${data.quantity}`);
  return parts.join(' ');
}

/**
 * Gerador de Código ZPL II (Zebra Programming Language)
 * Com escape estrito anti-injeção, GS1 DataMatrix com escape FNC1 (^FH_) e comando RFID (^RFW)
 */
export function generateZplLabel(options: ZplLabelOptions): string {
  const dpi = options.dpi || 203;
  const dotsPerMm = dpi === 300 ? 11.8 : 8.0;

  const widthDots = Math.round((options.widthMm || 100) * dotsPerMm);
  const heightDots = Math.round((options.heightMm || 60) * dotsPerMm);

  // Sanitização anti-injeção ZPL de todos os campos textuais e datas
  const safeCompany = escapeZpl(options.companyName).toUpperCase().substring(0, 38);
  const safeSku = escapeZpl(options.productCode);
  const safeProdName = escapeZpl(options.productName).substring(0, 34);
  const safeBatch = escapeZpl(options.batchNumber || 'LOTE-PADRAO');
  const safeUnit = escapeZpl(options.unit || 'UN').toUpperCase();
  const safeFabDate = escapeZpl(options.productionDate || 'N/A');
  const safeExpDate = escapeZpl(options.expiryDate || 'INDETERMINADA');
  const safeQty = escapeZpl(options.quantity);

  const gtin = (options.gtin || '07891234567890').replace(/\D/g, '').padStart(14, '0');
  const expGs1 = formatGs1Date(options.expiryDate);
  const fabGs1 = formatGs1Date(options.productionDate || new Date().toISOString().substring(0, 10));

  // Status de Qualidade formatado
  let qaBadge = 'INSP: PENDENTE';
  if (options.qaStatus === 'approved') qaBadge = 'SGQ: APROVADO [LIBERADO]';
  if (options.qaStatus === 'conditionally_approved') qaBadge = 'SGQ: APROV. CONDICIONAL';
  if (options.qaStatus === 'quarantine') qaBadge = 'SGQ: EM QUARENTENA';
  if (options.qaStatus === 'rejected') qaBadge = 'SGQ: REPROVADO [BLOQUEADO]';

  // GS1 DataMatrix com separador FNC1 hex (_1D) habilitado via ^FH_
  // No padrão GS1, o primeiro elemento após o cabeçalho é precedido por FNC1
  const gs1DataMatrixPayload = `_1D01${gtin}10${safeBatch}_1D17${expGs1}`;

  const copies = Math.max(1, options.copies || 1);

  // Função interna para construir uma etiqueta ZPL individual com EPC específico
  const buildSingleZpl = (rfidHexToUse?: string, printQty: number = 1) => {
    let rfidZplBlock = '';
    if (rfidHexToUse && /^[0-9A-Fa-f]{24}$/.test(rfidHexToUse)) {
      rfidZplBlock = `^RS8,,,1\n^RFW,H,1,2,6^FD${rfidHexToUse.toUpperCase()}^FS\n^FO30,238^A0N,16,16^FDRFID EPC: ${rfidHexToUse.toUpperCase()}^FS`;
    }

    return `^XA
^PW${widthDots}
^LL${heightDots}
^LH0,0

${rfidZplBlock}

^FO30,25^A0N,26,26^FD${safeCompany}^FS
^FO30,55^A0N,20,20^FDSKU: ${safeSku}^FS
^FO30,80^A0N,24,24^FD${safeProdName}^FS

^FO30,115^GB${widthDots - 60},2,2^FS

^FO30,130^A0N,22,22^FDLOTE: ${safeBatch}^FS
^FO30,158^A0N,18,18^FDFAB: ${safeFabDate}  VAL: ${safeExpDate}^FS
^FO30,182^A0N,20,20^FDQTD: ${safeQty} ${safeUnit}^FS
^FO30,210^A0N,20,20^FD${qaBadge}^FS

^FO${widthDots - 210},125^FH_^BXN,6,200,0,0,1
^FD${gs1DataMatrixPayload}^FS

^FO30,${heightDots - 130}^BCN,65,Y,N,N
^FD>;>801${gtin}10${safeBatch}^FS

^PQ${printQty}
^XZ`;
  };

  // Se RFID estiver ativo e cópias > 1, gera blocos individualizados com serial incrementado (sem colisão de tags)
  if (options.rfidHex && copies > 1 && options.serialNumber !== undefined && options.gtin) {
    const blocks: string[] = [];
    for (let i = 0; i < copies; i++) {
      const currentSerial = options.serialNumber + i;
      const currentEpc = encodeEpcSgtin96FromGtin({
        gtin: options.gtin,
        serialNumber: currentSerial
      });
      blocks.push(buildSingleZpl(currentEpc.hex, 1));
    }
    return blocks.join('\n\n').trim();
  }

  // Padrão (etiqueta normal ou RFID de cópia única)
  return buildSingleZpl(options.rfidHex, copies).trim();
}

/**
 * Codificador Oficial EPC SGTIN-96 (GS1 Tag Data Standard v1.13)
 * Estrutura binária exata de 96 bits:
 * - Header: 8 bits (0x30 = SGTIN-96)
 * - Filter: 3 bits (0..7, default 3: item level)
 * - Partition: 3 bits (5 = 24-bit Company Prefix, 20-bit Item Reference)
 * - Company Prefix: 24 bits (até 7 dígitos)
 * - Item Reference: 20 bits (até 6 dígitos)
 * - Serial Number: 38 bits (inteiro sequencial até 274.877.906.943)
 */
export function encodeEpcSgtin96(params: {
  companyPrefix: string;  // GS1 Company Prefix (e.g. "7891234")
  itemReference: string;  // Código numérico do item/SKU
  serialNumber: number;   // Serial sequencial único
  filter?: number;        // 3 bits (default 3: item level)
}): { hex: string; uri: string } {
  const header = 0x30n; // 8 bits: 00110000 (SGTIN-96)
  const filter = BigInt(params.filter ?? 3) & 0x07n; // 3 bits
  const partition = 5n; // 3 bits: Partition 5 (24-bit company, 20-bit item)

  // Extrai dígitos numéricos (com padding à esquerda)
  const prefixDigits = params.companyPrefix.replace(/\D/g, '').padStart(7, '0').slice(-7);
  const itemDigits = params.itemReference.replace(/\D/g, '').padStart(6, '0').slice(-6);

  const companyPrefixNum = BigInt(prefixDigits) & 0x00FFFFFFn; // 24 bits max (16.777.215)
  const itemRefNum = BigInt(itemDigits) & 0x000FFFFFn;         // 20 bits max (1.048.575)
  const serialNum = BigInt(Math.max(0, params.serialNumber)) & 0x3FFFFFFFFFn; // 38 bits max

  // Empacotamento em 96 bits exatos:
  // [Header: 8] [Filter: 3] [Partition: 3] [CompanyPrefix: 24] [ItemRef: 20] [Serial: 38]
  // 8 + 3 + 3 + 24 + 20 + 38 = 96 bits
  const epcBits =
    (header << 88n) |
    (filter << 85n) |
    (partition << 82n) |
    (companyPrefixNum << 58n) |
    (itemRefNum << 38n) |
    serialNum;

  const hex = epcBits.toString(16).toUpperCase().padStart(24, '0');
  const uri = `urn:epc:tag:sgtin-96:${filter}.${prefixDigits}.${itemDigits}.${params.serialNumber}`;

  return { hex, uri };
}

/**
 * Decodificador EPC SGTIN-96 a partir dos 96 bits (24 caracteres hex)
 */
export function decodeEpcSgtin96(hex: string): {
  isSgtin96: boolean;
  rawHex: string;
  filter?: number;
  companyPrefix?: string;
  itemReference?: string;
  serialNumber?: number;
} {
  const clean = (hex || '').replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
  if (clean.length !== 24 || !clean.startsWith('30')) {
    return { isSgtin96: false, rawHex: clean };
  }

  try {
    const epcBits = BigInt(`0x${clean}`);

    const header = Number((epcBits >> 88n) & 0xFFn);
    if (header !== 0x30) {
      return { isSgtin96: false, rawHex: clean };
    }

    const filter = Number((epcBits >> 85n) & 0x07n);
    const partition = Number((epcBits >> 82n) & 0x07n);

    if (partition === 5) {
      const companyPrefixNum = (epcBits >> 58n) & 0x00FFFFFFn;
      const itemRefNum = (epcBits >> 38n) & 0x000FFFFFn;
      const serialNum = epcBits & 0x3FFFFFFFFFn;

      return {
        isSgtin96: true,
        rawHex: clean,
        filter,
        companyPrefix: companyPrefixNum.toString().padStart(7, '0'),
        itemReference: itemRefNum.toString().padStart(6, '0'),
        serialNumber: Number(serialNum)
      };
    }

    return { isSgtin96: true, rawHex: clean, filter };
  } catch (err) {
    return { isSgtin96: false, rawHex: clean };
  }
}

/**
 * Codificador Oficial EPC SGTIN-96 a partir do GTIN-14 real (GS1 TDS v1.13)
 * No particionamento 5:
 * - GTIN-14: [Indicator: 1 dígito] [CompanyPrefix: 7 dígitos] [ItemReference: 5 dígitos] [CheckDigit: 1 dígito descartado]
 * - Item Reference de 20 bits no TDS = Indicator + 5 dígitos do item (6 dígitos totais)
 */
export function encodeEpcSgtin96FromGtin(params: {
  gtin: string;
  serialNumber: number;
  filter?: number;
}): { hex: string; uri: string } {
  const cleanGtin = (params.gtin || '').replace(/\D/g, '').padStart(14, '0');
  const indicator = cleanGtin[0];
  const companyPrefix = cleanGtin.substring(1, 8);
  const itemDigits = cleanGtin.substring(8, 13);
  const itemReference = `${indicator}${itemDigits}`;

  return encodeEpcSgtin96({
    companyPrefix,
    itemReference,
    serialNumber: params.serialNumber,
    filter: params.filter ?? 3
  });
}

const CODE128_PATTERNS = [
  "212222","222122","222221","121223","121322","131222","122213","122312","132212","221213",
  "221312","231212","112232","122132","122231","113222","123122","123221","223211","221132",
  "221231","213212","223112","312131","311222","321122","321221","312212","322112","322211",
  "212123","212321","232121","111323","131123","131321","112313","132113","132311","211313",
  "231113","231311","112133","112331","132131","113123","113321","133121","313121","211331",
  "231131","213113","213311","213131","311123","311321","331121","312113","312311","332111",
  "314111","221411","431111","111224","111422","121124","121421","141122","141221","112214",
  "112412","122114","122411","142112","142211","241211","221114","413111","241112","134111",
  "111242","121142","121241","114212","124112","124211","411212","421112","421211","212141",
  "214121","412121","111143","111341","131141","114113","114311","411113","411311","113141",
  "114131","311141","411131","211412","211214","211232","2331112"
];

/**
 * Sanitiza texto para injeção segura em nós de texto e atributos XML / SVG
 * Previne ataques XSS e quebra de marcação ao renderizar SVG com dangerouslySetInnerHTML
 */
export function escapeXml(text: string | number | undefined | null): string {
  if (text === undefined || text === null) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Gera código de barras 1D Code 128 / GS1-128 nativo em SVG
 * Padronizado para impressão física nítida e leitura óptica no navegador
 * Suporta caractere de controle FNC1 (código 102) quando formatado como GS1
 */
export function generateCode128Svg(text: string, options: { height?: number; barWidth?: number; includeText?: boolean; isGs1?: boolean } = {}): string {
  const height = options.height || 45;
  const barWidth = options.barWidth || 1.5;
  const includeText = options.includeText ?? true;
  const isGs1 = options.isGs1 ?? (text.includes('(') && text.includes(')'));

  if (!text) return '';

  // Inicia com Start B (104)
  const codes: number[] = [104];
  let checksum = 104;

  let streamToEncode = text;
  if (isGs1) {
    // Insere caractere FNC1 (código 102) na primeira posição para que o leitor identifique como GS1-128 (prefixo ]C1)
    codes.push(102);
    checksum = (checksum + 102 * 1) % 103;
    streamToEncode = text.replace(/[()]/g, '');
  }

  for (let i = 0; i < streamToEncode.length; i++) {
    const charCode = streamToEncode.charCodeAt(i);
    const val = Math.max(0, Math.min(95, charCode - 32));
    codes.push(val);
    const weight = isGs1 ? i + 2 : i + 1;
    checksum = (checksum + val * weight) % 103;
  }

  codes.push(checksum);
  codes.push(106); // Stop

  let currentX = 10;
  const rects: string[] = [];

  for (const code of codes) {
    const pattern = CODE128_PATTERNS[code];
    if (!pattern) continue;

    for (let p = 0; p < pattern.length; p++) {
      const w = parseInt(pattern[p], 10) * barWidth;
      if (p % 2 === 0) {
        rects.push(`<rect x="${currentX.toFixed(1)}" y="0" width="${w.toFixed(1)}" height="${height}" fill="black" />`);
      }
      currentX += w;
    }
  }

  currentX += 10;
  const totalWidth = currentX;
  const totalHeight = includeText ? height + 14 : height;
  const textElement = includeText
    ? `<text x="${(totalWidth / 2).toFixed(1)}" y="${totalHeight - 2}" text-anchor="middle" font-family="monospace" font-size="11" fill="black">${escapeXml(text)}</text>`
    : '';

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${totalWidth.toFixed(1)} ${totalHeight}" width="100%" height="${totalHeight}" preserveAspectRatio="xMidYMid meet">${rects.join('')}${textElement}</svg>`;
}
